// control-center/src/components/credentials/CreateBotDialog.tsx
import { useState } from 'react';

const KNOWN_PRODUCTS = ['Maestro', 'MOS', 'AgentMahoo', 'hermes-agent'] as const;
const BOT_NAME_REGEX = /^proj-[a-z0-9-]+-bot$/;

export interface CreateBotDialogProps {
  onSubmit: (body: { bot_name: string; product: string; ssh_pubkey?: string }) => Promise<void>;
  onCancel: () => void;
}

export function CreateBotDialog({ onSubmit, onCancel }: CreateBotDialogProps) {
  const [botName, setBotName] = useState('');
  const [product, setProduct] = useState<string>('Maestro');
  const [sshPubkey, setSshPubkey] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isValidName = BOT_NAME_REGEX.test(botName);

  const handleSubmit = async () => {
    if (!isValidName) {
      setError('Bot name must match ^proj-[a-z0-9-]+-bot$');
      return;
    }
    setPending(true);
    setError(null);
    try {
      await onSubmit({ bot_name: botName, product, ssh_pubkey: sshPubkey || undefined });
    } catch (e) {
      setError((e as Error).message);
      setPending(false);
    }
  };

  return (
    <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
      <div style={{ background: '#1e293b', color: '#e2e8f0', borderRadius: 8, padding: '1.5rem', maxWidth: 460, width: '100%' }}>
        <h3 style={{ marginTop: 0 }}>Create New Bot User</h3>
        {error && <div style={{ color: '#f87171', marginBottom: '0.75rem', fontSize: '0.875rem' }}>{error}</div>}
        <div style={{ marginBottom: '0.75rem' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Bot name</label>
          <input
            value={botName}
            onChange={e => setBotName(e.target.value)}
            placeholder="proj-myproject-bot"
            style={{ width: '100%', background: '#0f172a', color: '#e2e8f0', border: `1px solid ${isValidName || !botName ? '#334155' : '#ef4444'}`, borderRadius: 4, padding: '0.4rem 0.6rem', fontSize: '0.875rem', boxSizing: 'border-box' }}
          />
          {!isValidName && botName && <div style={{ color: '#f87171', fontSize: '0.75rem', marginTop: '0.25rem' }}>Must match: proj-[a-z0-9-]+-bot</div>}
        </div>
        <div style={{ marginBottom: '0.75rem' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Product</label>
          <select
            value={product}
            onChange={e => setProduct(e.target.value)}
            style={{ width: '100%', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 4, padding: '0.4rem 0.6rem', fontSize: '0.875rem' }}
          >
            {KNOWN_PRODUCTS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}>SSH public key (optional)</label>
          <textarea
            value={sshPubkey}
            onChange={e => setSshPubkey(e.target.value)}
            placeholder="ssh-ed25519 AAAA..."
            rows={3}
            style={{ width: '100%', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 4, padding: '0.4rem 0.6rem', fontSize: '0.8rem', resize: 'vertical', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} disabled={pending} style={{ background: '#374151', color: '#d1d5db', border: 'none', borderRadius: 4, padding: '0.4rem 1rem', cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSubmit} disabled={pending || !isValidName} style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 4, padding: '0.4rem 1rem', cursor: 'pointer', opacity: pending || !isValidName ? 0.6 : 1 }}>
            {pending ? 'Creating...' : 'Create Bot'}
          </button>
        </div>
      </div>
    </div>
  );
}
